import React, { useEffect, useState } from "react";
import { Box, CssBaseline, IconButton } from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { Close as CloseIcon } from "@material-ui/icons";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import { SnackbarProvider } from "notistack";
import AuthDialogs from "features/auth/AuthDialogs";
import CalendarView from "features/calendar/CalendarView";
import DocumentsView from "features/admin/DocumentsView";
import ActivitiesView from "features/admin/ActivitiesView";
import DashboardLoginView from "features/auth/DashboardLoginView";
import EmbeddedSIPFormView from "features/admin/EmbeddedSIPFormView";
import MembersView from "features/admin/MembersView";
import ClubView from "features/admin/ClubView";
import CourtsView from "features/admin/CourtsView";
import BookingsView from "features/admin/BookingsView";
import ProfileView from "features/user/ProfileView";
import OwnerSignupView from "features/admin/OwnerSignupView";
import HomeView from "features/home/HomeView";
import WelcomeView from "features/admin/WelcomeView";
import B2BFeaturesView from "features/admin/B2BFeaturesView";
import ChallengeView from "features/Challenge";
import GuestCheckinView from "features/checkin/GuestCheckinView";
import { View } from "features/global/reducer";
import { preferredLang } from "utils/localizer";
import { deDE, enUS } from "@material-ui/core/locale";
import { AuthLevel } from "features/auth/reducer";
import { getPersistedData, PersistedData } from "utils/persist";
import { Membership } from "api/memberships";
import TopBar from "./TopBar";
import Menu from "./Menu";
import styles from "./App.module.scss";
import "moment/locale/de";
import AutoSignOut from "./AutoSignOut";
import OfflineNotify from "./OfflineNotify";
import ErrorBoundary from "./ErrorBoundary";
// import ContractsUpdatedDialog from "./ContractsUpdatedDialog";

// localize moment
moment.locale(preferredLang);

// material-ui locales
const locales: any = { de: deDE, en: enUS };

// material-ui theme
const appTheme = createTheme({
    palette: {
        background: { default: styles.backgroundColor },
        primary: { main: styles.primaryColor },
        secondary: { main: styles.secondaryColor },
    },
}, locales[preferredLang]);

// prop types
interface AppProps {
    authLevel: AuthLevel,
    isReady: boolean,
    urlData: { [x: string]: string },
    userRole?: Membership["userRole"],
    isCompanyProfile?: boolean,
    view: View,
    onAuthorizeEmbedded: (token: string) => void,
    onAutoAuthorize: () => void,
    onNavigateTo: (view: View) => void,
    onSignOut: () => void,
}

// view permissions
const viewPermissions: { [view in View]: Membership["userRole"][] } = {
    [View.Activities]: ["owner", "admin"],
    [View.Bookings]: ["owner", "admin", "trainer"],
    [View.Calendar]: ["owner", "admin", "trainer", "groundskeeper", "standard"],
    [View.Club]: ["owner", "admin"],
    [View.Courts]: ["owner", "admin"],
    [View.Documents]: ["owner", "admin"],
    [View.EmbeddedSIPForm]: [],
    [View.GuestCheckin]: [],
    [View.Home]: ["owner", "admin", "trainer", "groundskeeper", "standard"],
    [View.Members]: ["owner", "admin"],
    [View.OwnerSignup]: [],
    [View.Profile]: ["owner", "admin", "trainer", "groundskeeper", "standard"],
    [View.Welcome]: ["owner", "admin"],
    [View.CompanySignup]: [],
    [View.B2BFeatures]: ["owner"],
    [View.Challenge]: ["owner"],
};

// main app layout
const App = ({ authLevel, isReady, urlData, userRole, isCompanyProfile, view, onAuthorizeEmbedded, onAutoAuthorize, onNavigateTo, onSignOut }: AppProps) => {
    // ui state
    const [showMenu, setShowMenu] = useState(true);

    // did mount hook -> auto authorization
    useEffect(() => { if (authLevel === AuthLevel.Unauthorized) onAutoAuthorize(); }, [authLevel]); // eslint-disable-line

    // user role -> view permitted?
    useEffect(() => {
        if (view === View.OwnerSignup || view === View.GuestCheckin || view === View.EmbeddedSIPForm || view === View.CompanySignup) {
            // all right, do nothing
        } else if (view === View.Calendar && urlData.embed) {
            onAuthorizeEmbedded(urlData.embed);
        } else if (authLevel === AuthLevel.User && userRole && !viewPermissions[view].includes(userRole)) {
            onNavigateTo(View.Calendar); // default redirect
        } else if (authLevel === AuthLevel.Dashboard && view !== View.Calendar) {
            onNavigateTo(View.Calendar); // default redirect
        }
    }, [authLevel, isReady, userRole, urlData, view]); // eslint-disable-line

    // notistack global close action
    const notistackRef = React.createRef<any>();
    const closeAction = (key: string) => (
        <IconButton
            onClick={() => notistackRef.current.closeSnackbar(key)}
            size="small"
            className={styles.snackbar}
        >
            <CloseIcon />
        </IconButton>
    );

    // render
    const showMenuIfUser = showMenu && (authLevel === AuthLevel.User);
    return (
        <ThemeProvider theme={appTheme}>
            <ErrorBoundary>
                <MuiPickersUtilsProvider libInstance={moment} locale={preferredLang} utils={MomentUtils}>
                    <SnackbarProvider action={closeAction} ref={notistackRef}>
                        <Box className={styles.root}>
                            {/* general css settings */}
                            <CssBaseline />

                            {/* main view */}
                            {!isCompanyProfile && (isReady && view !== View.OwnerSignup && view !== View.GuestCheckin && view !== View.EmbeddedSIPForm && view !== View.CompanySignup) ? (
                                <>
                                    {/* top app bar */}
                                    <TopBar showMenu={showMenuIfUser} onToggleMenu={() => setShowMenu(!showMenu)} />

                                    {/* menu drawer */}
                                    <Menu showMenu={showMenuIfUser} onToggleMenu={() => setShowMenu(!showMenu)} />

                                    {/* content */}
                                    <Box className={styles.content}>
                                        {(view === View.Activities) && <ActivitiesView />}
                                        {(view === View.Bookings) && <BookingsView />}
                                        {(view === View.Calendar) && <CalendarView embedded={!!urlData.embed} showMenu={showMenuIfUser} />}
                                        {(view === View.Club) && <ClubView />}
                                        {(view === View.B2BFeatures) && <B2BFeaturesView />}
                                        {(view === View.Challenge) && <ChallengeView />}
                                        {(view === View.Courts) && <CourtsView />}
                                        {(view === View.Documents) && <DocumentsView />}
                                        {(view === View.Home) && <HomeView />}
                                        {(view === View.Members) && <MembersView />}
                                        {(view === View.Profile) && <ProfileView />}
                                        {(view === View.Welcome) && <WelcomeView />}
                                    </Box>
                                </>
                            ) : (
                                <>
                                    {(view === View.OwnerSignup) ? ( // eslint-disable-line
                                        <OwnerSignupView />
                                    ) : (view === View.CompanySignup) ? ( // eslint-disable-line
                                        <OwnerSignupView isCompany />
                                    ) : (view === View.GuestCheckin) ? ( // eslint-disable-line
                                        <GuestCheckinView />
                                    ) : (view === View.EmbeddedSIPForm) ? (
                                        <EmbeddedSIPFormView />
                                    ) : (
                                        <DashboardLoginView />
                                    )}
                                </>
                            )}

                            {/* dialogs etc. */}
                            <AuthDialogs />
                            {/* As per discussion just deactivating this dialogbox, may be in future we can re-activate */}
                            {/* {userRole === "owner" && <ContractsUpdatedDialog />} */}
                            <OfflineNotify />

                            {/* auto logout in dashboard mode */}
                            <AutoSignOut
                                disabled={!(getPersistedData(PersistedData.DashboardToken) && authLevel === AuthLevel.User && process.env.REACT_APP_MODE === "prod")}
                                onSignOut={onSignOut}
                            />
                        </Box>
                    </SnackbarProvider>
                </MuiPickersUtilsProvider>
            </ErrorBoundary>
        </ThemeProvider>
    );
};

export default App;
